import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Get } from "../config/api/get";
import { Post } from "../config/api/post";
import { useSelector } from "react-redux";
import { CATEGORY } from "../config/constants";

export const CATEGORY_KEY = ["categories"];

function apiErrorMessage(error, fallback) {
  return error?.data?.message || error?.message || fallback;
}

function parseListResponse(response, fallbackMessage) {
  if (!response || typeof response !== "object") {
    throw new Error(fallbackMessage);
  }
  if (response.status === false) {
    throw new Error(response.message || fallbackMessage);
  }
  if (response.isAxiosError || response.name === "AxiosError") {
    throw new Error(apiErrorMessage(response, fallbackMessage));
  }
  if (Array.isArray(response.docs)) {
    return response;
  }
  if (Array.isArray(response.data?.docs)) {
    return response.data;
  }
  return { docs: [], totalDocs: 0 };
}

function parseActionResponse(response, fallbackMessage) {
  if (!response || typeof response !== "object") {
    throw new Error(fallbackMessage);
  }
  if (response.status === false) {
    throw new Error(response.message || fallbackMessage);
  }
  if (response.isAxiosError || response.name === "AxiosError") {
    throw new Error(apiErrorMessage(response, fallbackMessage));
  }
  return response;
}

function parsePostResponse(response, fallbackMessage) {
  const body = response?.data;
  if (!body || body.status === false) {
    throw new Error(body?.message || fallbackMessage);
  }
  return body;
}

const invalidateCategories = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: CATEGORY_KEY });
};

export const useGetAllCategories = (params = { page: 1, limit: 100, keyword: "" }) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: [...CATEGORY_KEY, params],
    queryFn: async () => {
      const response = await Get(CATEGORY.getAllCategories, token, {
        page: String(params.page || 1),
        limit: String(params.limit || 100),
        keyword: params.keyword || "",
      });
      return parseListResponse(response, "Failed to load categories");
    },
    enabled: !!token,
    placeholderData: keepPreviousData,
  });
};

export const useGetCategoryById = (id) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["category", id],
    queryFn: async () => {
      const response = await Get(CATEGORY.getCategoryById + id, token);
      const parsed = parseActionResponse(response, "Failed to load category");
      return parsed?.data?.category || parsed?.data;
    },
    enabled: !!token && !!id,
  });
};

export const useAddCategory = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (payload) => {
      const response = await Post(CATEGORY.addCategory, payload, token);
      return parsePostResponse(response, "Failed to add category");
    },
    onSuccess: () => invalidateCategories(queryClient),
  });
};

export const useUpdateCategory = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const response = await Post(CATEGORY.updateCategory + id, payload, token);
      return parsePostResponse(response, "Failed to update category");
    },
    onSuccess: () => invalidateCategories(queryClient),
  });
};

export const useToggleCategoryStatus = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (id) => {
      const response = await Get(CATEGORY.toggleStatus + id, token);
      return parseActionResponse(response, "Failed to update status");
    },
    onSuccess: () => invalidateCategories(queryClient),
  });
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (id) => {
      const response = await Get(CATEGORY.deleteCategory + id, token);
      return parseActionResponse(response, "Failed to delete category");
    },
    onSuccess: () => invalidateCategories(queryClient),
  });
};
